// Seasonal calibration — learn the account's week-of-year seasonal curve from a
// year of actuals and compare it to the built-in default. Seasonality is the
// "shape" of the year (peaks/dips); base + DOW calibration own the level.
//
// For each actual we compute the expected volume WITHOUT seasonality
// (base × growth × allocation × DOW) and let the engine derive the per-week
// multiplier. Apply saves the curve (used by every forecast); Reset reverts.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtN, today } from "../../lib/format";
import type { Customer } from "../../lib/types";
import {
  defaultSeasonalFactors,
  explainCustomerHub,
  learnSeasonalFactors,
  setSeasonalFactors,
  weekOfYearOf,
} from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";
import { clearSeasonal, getSeasonal, saveSeasonal } from "./seasonalApi";

export default function SeasonalPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const nowYear = Number(today().slice(0, 4));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [savedExists, setSavedExists] = useState(false);
  const [year, setYear] = useState(nowYear);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = [nowYear - 1, nowYear, nowYear + 1];

  const load = async () => {
    if (!accountId) return;
    try {
      const [c, a, saved] = await Promise.all([
        listCustomers(accountId),
        listAccountCustomerActuals(accountId, `${year}-01-01`, `${year}-12-31`),
        getSeasonal(accountId),
      ]);
      setCustomers(c);
      setActuals(a);
      setSavedExists(saved != null);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load seasonal data."));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, year]);

  const custById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);
  const def = defaultSeasonalFactors();

  // Observations: actual paired with expected volume excluding seasonality.
  const obs = useMemo(() => {
    const out: Array<{ date: string; actual: number; expected: number }> = [];
    for (const a of actuals) {
      const c = custById.get(a.customerId);
      if (!c) continue;
      const b = explainCustomerHub(c, a.hubId, a.date);
      const expected = b.base * b.growthFactor * (b.allocationPct / 100) * b.dowFactor;
      if (expected > 0) out.push({ date: a.date, actual: a.actualVolume, expected });
    }
    return out;
  }, [actuals, custById]);

  const learned = useMemo(() => learnSeasonalFactors(obs), [obs]);
  const dataWeeks = useMemo(() => new Set(obs.map((o) => weekOfYearOf(o.date))), [obs]);

  async function apply() {
    if (!accountId || !learned) return;
    setBusy(true);
    setError(null);
    try {
      await saveSeasonal(accountId, learned);
      setSeasonalFactors(learned); // take effect immediately this session
      setSavedExists(true);
      setMsg("Calibrated seasonal curve applied — forecasts now use it.");
    } catch (e) {
      setError(getErrorMessage(e, "Failed to save seasonal curve."));
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!accountId) return;
    if (!confirm("Revert to the built-in default seasonal curve?")) return;
    setBusy(true);
    setError(null);
    try {
      await clearSeasonal(accountId);
      setSeasonalFactors(null);
      setSavedExists(false);
      setMsg("Reverted to the default seasonal curve.");
    } catch (e) {
      setError(getErrorMessage(e, "Failed to reset."));
    } finally {
      setBusy(false);
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Seasonal calibration</h1>
        {msg && <span className="text-sm text-emerald-600">{msg}</span>}
      </div>
      <p className="text-xs text-slate-500">
        Learns the year's shape (week-of-year peaks/dips) from actuals and compares it to the
        built-in default. Base + day-of-week calibration own the level; this owns the seasonal
        shape. Weeks without actuals keep the default.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-end gap-3">
            <Field label="Learn from year">
              <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="pb-2 text-sm text-slate-600">
              <div>
                Currently:{" "}
                <span className={savedExists ? "font-medium text-brand-700" : "text-slate-500"}>
                  {savedExists ? "calibrated curve" : "built-in default"}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                {obs.length} actual-days · {dataWeeks.size} of 52 weeks have data
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Button onClick={apply} disabled={busy || !learned}>
              {busy ? "…" : "Apply calibrated curve"}
            </Button>
            <Button variant="ghost" onClick={reset} disabled={busy || !savedExists}>
              Reset to default
            </Button>
          </div>
        </div>
        {!learned && (
          <p className="mt-2 text-xs text-amber-700">
            Not enough actuals in {year} to calibrate — enter actuals on the Actuals page first.
          </p>
        )}
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-medium">Week</th>
              <th className="px-3 py-2 text-right font-medium">Default</th>
              <th className="px-3 py-2 text-right font-medium">Calibrated</th>
              <th className="px-3 py-2 text-right font-medium">Δ</th>
              <th className="px-4 py-2 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => {
              const d = def[w];
              const c = learned ? learned[w] : d;
              const fromData = dataWeeks.has(w);
              const delta = c - d;
              return (
                <tr
                  key={w}
                  className={`border-b border-slate-100 last:border-0 ${fromData ? "bg-brand-50/30" : ""}`}
                >
                  <td className="px-4 py-1.5 font-medium text-slate-900">W{w}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">×{d.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-900">
                    ×{c.toFixed(2)}
                  </td>
                  <td
                    className={`px-3 py-1.5 text-right tabular-nums ${
                      Math.abs(delta) < 0.005
                        ? "text-slate-300"
                        : delta > 0
                          ? "text-emerald-600"
                          : "text-red-600"
                    }`}
                  >
                    {Math.abs(delta) < 0.005 ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`}
                  </td>
                  <td className="px-4 py-1.5">
                    {fromData ? (
                      <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        from actuals
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">default</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-slate-400">
        Total recorded volume in {year}: {fmtN(obs.reduce((s, o) => s + o.actual, 0))} pkg across{" "}
        {obs.length} customer-days.
      </p>
    </div>
  );
}
